import React from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Stack, Typography } from '@mui/material'
import { keyframes } from '@mui/material/styles'

import { LiveEventStatus } from '../helpers/blueprintEventRows'

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 currentColor; opacity: 1; }
  70% { box-shadow: 0 0 0 6px transparent; opacity: 0.85; }
  100% { box-shadow: 0 0 0 0 transparent; opacity: 1; }
`

const STATUS_COLOR: Record<LiveEventStatus, string> = {
  active: 'success.main',
  ended: 'primary.main',
  upcoming: 'text.disabled',
}

interface StatusDotProps {
  color: string
  pulsing?: boolean
}

/** 8px status marker; `pulsing` adds the soft ring animation used for "N live". */
export const StatusDot: React.FC<StatusDotProps> = ({ color, pulsing }) => (
  <Box
    component="span"
    aria-hidden
    sx={{
      width: 8,
      height: 8,
      borderRadius: '50%',
      bgcolor: color,
      color,
      flexShrink: 0,
      display: 'inline-block',
      ...(pulsing ? { animation: `${pulse} 1.8s ease-out infinite`, '@media (prefers-reduced-motion: reduce)': { animation: 'none' } } : {}),
    }}
  />
)

interface EventStatusDotProps {
  status: LiveEventStatus
}

/** Dot + label for one event's status: green Live, purple Ended, grey Upcoming. */
export const EventStatusDot: React.FC<EventStatusDotProps> = React.memo(({ status }) => {
  const { t } = useTranslation()
  const label =
    status === 'active'
      ? t('market-analytics:event_status_live')
      : status === 'upcoming'
      ? t('market-analytics:event_status_upcoming')
      : t('market-analytics:event_status_ended')
  return (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <StatusDot color={STATUS_COLOR[status]} />
      <Typography variant="caption" color="text.secondary" component="span">
        {label}
      </Typography>
    </Stack>
  )
})
EventStatusDot.displayName = 'EventStatusDot'
